import { Fragment, useLayoutEffect, useRef, useState } from "react";

import Spinner from "components/Spinner";

const TARGET_TABLE_WIDTH_PX = 1245;
const MIN_REPORT_PREVIEW_HEIGHT_PX = 520;

/*
 * Ud1000SummaryTable 역할:
 * - UD1000 보고서의 요약 정보와 진단 이미지를 표 형태로 렌더링한다.
 * - 읽기/편집 모드와 관계없이 부모가 계산한 표시 데이터를 그대로 화면에 반영한다.
 * - 이미지가 아직 없을 때는 각 슬롯에서 동일한 로딩 UI를 유지한다.
 */
export default function Ud1000SummaryTable({
  draftData,
  file,
  t,
  disableResponsiveScale = false,
  maxViewportHeight = null,
  imgTemp,
  previewScale = 1,
  displayFusionLabel,
  ultrasonicRef,
  thermalRef,
  fftRef,
  uvRef,
}) {
  const tableWrapRef = useRef(null);
  const tableStageRef = useRef(null);
  const [responsiveLayout, setResponsiveLayout] = useState({
    scale: 1,
    height: null,
  });
  const numericPreviewScale = Number(previewScale);
  const previewScaleMultiplier =
    Number.isFinite(numericPreviewScale) && numericPreviewScale > 0
      ? numericPreviewScale
      : 1;

  useLayoutEffect(() => {
    if (disableResponsiveScale) {
      setResponsiveLayout({
        scale: 1,
        height: null,
      });
      return undefined;
    }

    const wrapElement = tableWrapRef.current;
    const stageElement = tableStageRef.current;

    if (!wrapElement || !stageElement) return undefined;

    let frameId = 0;

    const measure = () => {
      const widthScale = wrapElement.clientWidth / TARGET_TABLE_WIDTH_PX;
      const naturalHeight = stageElement.offsetHeight || 0;
      const heightScale =
        maxViewportHeight != null && maxViewportHeight > 0 && naturalHeight > 0
          ? maxViewportHeight / naturalHeight
          : 1;
      const nextScale =
        Math.min(1, widthScale, heightScale) * previewScaleMultiplier;
      const nextHeight = Number((naturalHeight * nextScale).toFixed(2));

      setResponsiveLayout((prev) => {
        const prevHeight = prev.height ?? 0;

        if (
          Math.abs(prev.scale - nextScale) < 0.001 &&
          Math.abs(prevHeight - nextHeight) < 0.5
        ) {
          return prev;
        }

        return {
          scale: nextScale,
          height: nextHeight,
        };
      });
    };

    const scheduleMeasure = () => {
      cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(measure);
    };

    scheduleMeasure();

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => {
            scheduleMeasure();
          });

    resizeObserver?.observe(wrapElement);
    resizeObserver?.observe(stageElement);
    window.addEventListener("resize", scheduleMeasure);

    return () => {
      cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", scheduleMeasure);
    };
  }, [disableResponsiveScale, maxViewportHeight, previewScaleMultiplier]);

  const clickPoints = Array.isArray(file?.click_points)
    ? file.click_points
    : [];
  const minPreviewHeight = disableResponsiveScale
    ? 0
    : Math.max(
        0,
        Math.min(
          maxViewportHeight ?? MIN_REPORT_PREVIEW_HEIGHT_PX,
          MIN_REPORT_PREVIEW_HEIGHT_PX,
        ),
      );

  return (
    <div
      ref={tableWrapRef}
      className={`ud1000ReportScaleWrap${
        disableResponsiveScale ? " is-fullSize" : ""
      }`}
      style={{
        "--ud1000-report-scale": responsiveLayout.scale,
        "--ud1000-report-table-width": `${TARGET_TABLE_WIDTH_PX}px`,
        "--ud1000-report-max-viewport-height":
          maxViewportHeight == null ? "none" : `${maxViewportHeight}px`,
        "--ud1000-report-min-height": `${minPreviewHeight}px`,
        "--ud1000-report-scaled-height":
          responsiveLayout.height == null
            ? "auto"
            : `${responsiveLayout.height}px`,
      }}
    >
      <div className="ud1000ReportScaleStage">
        <div ref={tableStageRef} className="ud1000ReportScaleContent">
          <table
            className="ud1000Report typeImg"
            spellCheck={false}
            style={{ width: `${TARGET_TABLE_WIDTH_PX}px` }}
          >
            <caption>{t("imageFileIndex.report.title")}</caption>
            <tbody>
              <tr>
                <th>{t("imageFileIndex.report.facilityType")}</th>
                <td>{draftData?.facility_type}</td>
                <th>{t("imageFileIndex.report.facilityName")}</th>
                <td colSpan={3}>{draftData?.facility_name}</td>
                <th>{t("imageFileIndex.report.partName")}</th>
                <td>{draftData?.part_name}</td>
              </tr>

              <tr>
                <th>{t("imageFileIndex.report.buildingName")}</th>
                <td>{draftData?.building_name}</td>
                <th>{t("imageFileIndex.report.floor")}</th>
                <td>{draftData?.floor}</td>
                <th>{t("imageFileIndex.report.location")}</th>
                <td>{draftData?.location}</td>
                <th>{t("imageFileIndex.report.shootingDate")}</th>
                <td>{draftData?.shooting_date}</td>
              </tr>

              <tr>
                <th style={{ whiteSpace: "pre-line" }}>
                  {t("imageFileIndex.report.decibel")}
                  <br />
                  <span>(dB)</span>
                </th>
                <td className="readOnly" id="avg_decibel">
                  {file?.avg_decibel}
                </td>

                <th style={{ whiteSpace: "pre-line" }}>
                  {t("imageFileIndex.report.temperature")}
                  <br />
                  <span>(℃)</span>
                </th>
                <td className="readOnly">
                  <div className="thermalValue">
                    {!clickPoints.length ? (
                      <>
                        {t("imageFileIndex.report.thermal.max")}:{" "}
                        {file?.thermal_max}
                        <br />
                        {t("imageFileIndex.report.thermal.min")}:{" "}
                        {file?.thermal_min}
                        <br />
                        {t("imageFileIndex.report.thermal.center")}:{" "}
                        {file?.thermal_center}
                      </>
                    ) : (
                      <>
                        {clickPoints.map((point, index) => (
                          <Fragment key={index}>
                            L{index + 1}: {point?.value ?? ""}
                            <br />
                          </Fragment>
                        ))}
                      </>
                    )}
                  </div>
                </td>

                <th>{t("imageFileIndex.report.uvDetected")}</th>
                <td className="readOnly">{file?.uv_detected}</td>
                <th style={{ whiteSpace: "pre-line" }}>
                  {t("imageFileIndex.report.fusionResultMultiline")}
                </th>
                <td>{displayFusionLabel}</td>
              </tr>

              <tr>
                <th>
                  {t("imageFileIndex.report.distance")}
                  <br />
                  <span>(m)</span>
                </th>
                <td className="readOnly">{file?.distance}</td>

                <th>
                  {t("imageFileIndex.report.ambientTemperature")}
                  <br />
                  <span>(℃)</span>
                </th>
                <td>{draftData?.ambient_temp}</td>

                <th>{t("imageFileIndex.report.equipmentUsed")}</th>
                <td>{draftData?.equipment_used}</td>

                <th>{t("imageFileIndex.report.serialNumber")}</th>
                <td>{draftData?.serial_number}</td>
              </tr>

              <tr>
                <th rowSpan={2}>
                  {t("imageFileIndex.report.inspectionResult.title")}
                  <br />
                  <span>
                    {t("imageFileIndex.report.inspectionResult.label")}
                  </span>
                </th>
                <td rowSpan={2} colSpan={7}>
                  <div className="inspection">
                    {draftData?.inspection_result}
                  </div>
                </td>
              </tr>
              <tr></tr>

              <tr className="fftLabelRow">
                <th colSpan={4}>
                  {t("imageFileIndex.report.ultrasonicDiagnosis")}
                </th>
                <th colSpan={4}>
                  {t("imageFileIndex.report.fastFourierTransform")}
                </th>
              </tr>
              <tr>
                <td className="imgCell" rowSpan={3} colSpan={4}>
                  {imgTemp?.image_visual ? (
                    <img
                      ref={ultrasonicRef}
                      style={{ aspectRatio: "640 / 435", height: "auto", objectFit: "fill" }}
                      src={imgTemp?.image_visual}
                      alt="ultrasonic shooting"
                    />
                  ) : (
                    <Spinner type="report" position="absolute" />
                  )}
                </td>
                <td className="imgCell" colSpan={4}>
                  {imgTemp.markup_image_graph ? (
                    <img
                      ref={fftRef}
                      src={imgTemp?.markup_image_graph}
                      alt="fftgraph"
                    />
                  ) : (
                    <Spinner type="report" position="absolute" />
                  )}
                </td>
              </tr>
              <tr style={{ height: "60px" }}>
                <th colSpan={4}>SPECTROGRAM</th>
              </tr>
              <tr>
                <td className="imgCell" colSpan={4}>
                  {imgTemp.image_spectrogram ? (
                    <img src={imgTemp?.image_spectrogram} alt="spectrogram" />
                  ) : (
                    <Spinner type="report" position="absolute" />
                  )}
                </td>
              </tr>
              <tr>
                <th colSpan={4}>
                  {t("imageFileIndex.report.thermalDiagnosis")}
                </th>
                <th colSpan={4}>
                  {t("imageFileIndex.report.ultravioletDiagnosis")}
                </th>
              </tr>
              <tr>
                <td className="imgCell" colSpan={4}>
                  {imgTemp.markup_image_thermal ? (
                    imgTemp.markup_image_triplespot &&
                    (draftData.click_points[0] || draftData.click_areas[0]) ? (
                      <img
                        ref={thermalRef}
                        style={{ aspectRatio: "640 / 435", height: "auto", objectFit: "fill" }}
                        src={imgTemp?.markup_image_triplespot}
                        alt="thermal shooting"
                      />
                    ) : (
                      <img
                        ref={thermalRef}
                        style={{ aspectRatio: "640 / 435", height: "auto", objectFit: "fill" }}
                        src={imgTemp?.markup_image_thermal}
                        alt="thermal shooting"
                      />
                    )
                  ) : (
                    <Spinner type="report" position="absolute" />
                  )}
                </td>
                <td className="imgCell" colSpan={4}>
                  {imgTemp.image_uv ? (
                    <img
                      ref={uvRef}
                      style={{ aspectRatio: "640 / 435", height: "auto", objectFit: "fill" }}
                      src={imgTemp?.image_uv}
                      alt="ultraviolet shooting"
                    />
                  ) : (
                    <Spinner type="report" position="absolute" />
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
